/**
 * Normalize a value to a string for CSV. Objects (e.g. API returning { id, name }) become a display string.
 * @param {string|number|object} value
 * @returns {string}
 */
function toCsvString(value) {
    if (value === null || value === undefined) return ''
    if (typeof value === 'object') {
        return (
            value?.name ??
            value?.label ??
            value?.value ??
            value?.displayName ??
            value?.sectionName ??
            value?.familyName ??
            value?.number ??
            value?.className ??
            ''
        )
    }
    return String(value)
}

/**
 * Escape a single CSV field per RFC 4180.
 * Wrap in double quotes if field contains comma, quote, or newline; double internal quotes.
 * @param {string|number|object} value
 * @returns {string}
 */
function escapeCsvField(value) {
    const str = toCsvString(value)
    const needsQuotes = /[",\r\n]/.test(str)
    if (!needsQuotes) return str
    return '"' + str.replace(/"/g, '""') + '"'
}

/**
 * Build a CSV string from headers and rows (array of arrays of strings/numbers).
 * Uses CRLF for line endings. Optionally prepends UTF-8 BOM for Excel.
 * @param {string[]} headers
 * @param {Array<Array<string|number>>} rows
 * @param {boolean} [prependBom=true]
 * @returns {string}
 */
export function buildCsv(headers, rows, prependBom = true) {
    const headerRow = headers.map(escapeCsvField).join(',')
    const dataRows = (rows || []).map((row) => row.map(escapeCsvField).join(','))
    const body = [headerRow, ...dataRows].join('\r\n')
    const bom = prependBom ? '\uFEFF' : ''
    return bom + body
}

/**
 * Trigger browser download of a CSV string as a file.
 * @param {string} csvString
 * @param {string} filename
 */
export function downloadCsv(csvString, filename) {
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.click()
    URL.revokeObjectURL(url)
}
